/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { getStorefrontConfig } from "lib/storefront";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { getAppleIdToken, getGoogleAccessToken, getMicrosoftIdToken } from "lib/socialAuth";

// Images
import vbPlain from "assets/images/logos/ShortLogo.png";

const getSafeRedirect = (locationState, isMobile) => {
  const from = locationState?.from?.pathname;
  // Use a neutral route that triggers App's role-based default redirect.
  const defaultPath = isMobile ? "/m/post-login" : "/post-login";
  // Always go through post-login so staff can select company.
  // The original destination (if any) is handled by PostLogin via sessionStorage.
  return defaultPath;
};

const socialIconButtonSx = {
  minWidth: 44,
  width: 44,
  height: 40,
  p: 0,
  borderColor: "rgba(15,23,42,0.16)",
  bgcolor: "#fff",
  borderRadius: 1.8,
  "&:hover": { bgcolor: "rgba(248,250,252,1)", borderColor: "rgba(15,23,42,0.24)" },
};

function MicrosoftMark() {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 7px)", gap: "2px" }}>
      <Box sx={{ width: 7, height: 7, bgcolor: "#F25022" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#7FBA00" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#00A4EF" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#FFB900" }} />
    </Box>
  );
}

const hasMsalInteractionMarker = () => {
  const storages = [typeof window !== "undefined" ? window.sessionStorage : null, typeof window !== "undefined" ? window.localStorage : null];
  for (const storage of storages) {
    if (!storage) continue;
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i) || "";
        if (key === "msal.interaction.status" || key.endsWith(".interaction.status")) {
          return true;
        }
      }
    } catch {
      // ignore storage access issues
    }
  }
  return false;
};

const resolveRedirectUriForCurrentHost = (configured, fallbackPath) => {
  const fallback = `${window.location.origin}${fallbackPath}`;
  const value = String(configured || "").trim();
  if (!value) return fallback;
  try {
    const parsed = new URL(value);
    if (parsed.host !== window.location.host) {
      const nextPath = parsed.pathname || fallbackPath;
      return `${window.location.origin}${nextPath}`;
    }
    return parsed.toString();
  } catch {
    return fallback;
  }
};

function Basic() {
  const storefront = getStorefrontConfig();
  const useSpanish = String(storefront?.locale || "").toLowerCase().startsWith("es");

  const text = useSpanish
    ? {
        siteLabel: "VBspecialties.com",
        signIn: "Iniciar sesión",
        email: "Correo",
        password: "Contraseña",
        rememberMe: "Recordarme",
        signingIn: "Iniciando…",
        secureSignIn: "Inicio seguro",
        continueWith: "o continuar con",
        socialNotConfigured: "no está configurado todavía.",
        socialFailed: "Error al iniciar sesión con proveedor social",
        invalidCredentials: "Credenciales inválidas",
        loginFailed: "Error al iniciar sesión",
        completingMicrosoft: "Completando inicio de sesión con Microsoft...",
        noAccount: "¿No tienes cuenta?",
        createAccount: "Crear cuenta",
      }
    : {
        siteLabel: "VBspecialties.com",
        signIn: "Sign In",
        email: "Email",
        password: "Password",
        rememberMe: "Remember me",
        signingIn: "Signing in...",
        secureSignIn: "Secure Sign In",
        continueWith: "or continue with",
        socialNotConfigured: "sign in is not configured yet.",
        socialFailed: "Social sign in failed",
        invalidCredentials: "Invalid credentials",
        loginFailed: "Login failed",
        completingMicrosoft: "Completing Microsoft sign in...",
        noAccount: "Don’t have an account?",
        createAccount: "Create one",
      };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithSocial, token } = useAuth();

  const googleClientId = String(
    process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || ""
  ).trim();
  const appleClientId = String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim();
  const microsoftClientId = String(process.env.REACT_APP_MICROSOFT_CLIENT_ID || "").trim();
  const microsoftTenantId = String(process.env.REACT_APP_MICROSOFT_TENANT_ID || "common").trim() || "common";
  const appleRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_APPLE_REDIRECT_URI,
    "/authentication/sign-in"
  );
  const microsoftRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
    "/msal-popup.html"
  );

  const isMobile = location.pathname === "/m" || location.pathname.startsWith("/m/");
  const redirectTo = getSafeRedirect(location.state, isMobile);
  const from = location.state?.from?.pathname;
  const isPopupWindow = typeof window !== "undefined" && !!window.opener;
  const msalSearch = typeof window !== "undefined" ? String(window.location.search || "") : "";
  const msalHash = typeof window !== "undefined" ? String(window.location.hash || "") : "";
  const hasMsalCallbackPayload = /(code=|id_token=|access_token=|error=|state=)/i.test(`${msalSearch}&${msalHash}`);
  const isPopupOnSignInRoute = isPopupWindow && location.pathname === "/authentication/sign-in";
  const isMsalPopupCallback = isPopupOnSignInRoute || (isPopupWindow && (hasMsalCallbackPayload || hasMsalInteractionMarker()));

  useEffect(() => {
    // Preserve original destination (if any) so PostLogin can continue after company selection.
    if (typeof from === "string" && from && !from.includes("/authentication/") && !from.endsWith("/sign-out")) {
      try {
        sessionStorage.setItem("postLoginNext", from);
      } catch {
        // ignore storage errors
      }
    } else {
      try {
        sessionStorage.removeItem("postLoginNext");
      } catch {
        // ignore
      }
    }
  }, [from]);

  useEffect(() => {
    // if already logged in, redirect to dashboard
    if (isMsalPopupCallback) return;
    if (token) {
      navigate(redirectTo, { replace: true });
    }
  }, [isMsalPopupCallback, navigate, token, redirectTo]);

  const handleSocialSignIn = async (provider) => {
    const normalized = String(provider || "").trim().toLowerCase();
    if (!normalized) return;
    const providerEnabled =
      (normalized === "google" && !!googleClientId) ||
      (normalized === "apple" && !!appleClientId) ||
      (normalized === "microsoft" && !!microsoftClientId);
    if (!providerEnabled) {
      setError(`${normalized[0].toUpperCase()}${normalized.slice(1)} ${text.socialNotConfigured}`);
      return;
    }
    try {
      setLoading(true);
      setError("");
      let payload = {};
      if (normalized === "google") {
        payload = await getGoogleAccessToken({ clientId: googleClientId });
      } else if (normalized === "apple") {
        payload = await getAppleIdToken({ clientId: appleClientId, redirectURI: appleRedirectUri });
      } else if (normalized === "microsoft") {
        payload = await getMicrosoftIdToken({
          clientId: microsoftClientId,
          tenantId: microsoftTenantId,
          redirectUri: microsoftRedirectUri,
        });
      } else {
        throw new Error("Unsupported provider");
      }

      const ok = await loginWithSocial({
        provider: normalized,
        ...payload,
        companyKey: storefront?.companyKey || undefined,
      });
      if (ok) navigate(redirectTo, { replace: true });
      else setError(text.socialFailed);
    } catch (socialError) {
      const details =
        socialError?.response?.data?.error?.message ||
        socialError?.response?.data?.error ||
        socialError?.response?.data?.message ||
        socialError?.message ||
        text.socialFailed;
      setError(String(details));
    } finally {
      setLoading(false);
    }
  };

  if (isMsalPopupCallback) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 58%, #ffffff 100%)",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 340,
            p: 2,
            borderRadius: 3,
            borderColor: "rgba(30,41,59,0.14)",
            boxShadow: "0 16px 40px rgba(15,23,42,0.12)",
            textAlign: "center",
          }}
        >
          <MDTypography variant="button" color="text" sx={{ opacity: 0.78 }}>
            {text.completingMicrosoft}
          </MDTypography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 2, sm: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 58%, #ffffff 100%)",
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 470,
          p: { xs: 2.25, sm: 2.75 },
          borderRadius: 4,
          borderColor: "rgba(30,41,59,0.14)",
          boxShadow: "0 26px 60px rgba(15,23,42,0.14)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.2} sx={{ mb: 1.4 }}>
          <MDBox component="img" src={vbPlain} alt="Valve Body Specialties" sx={{ height: 28, width: "auto", objectFit: "contain" }} />
          <Chip size="small" label={text.secureSignIn} sx={{ bgcolor: "rgba(37,99,235,0.1)", color: "#1d4ed8", fontWeight: 700 }} />
        </Stack>

        <MDTypography variant="h4" fontWeight={900} sx={{ color: "#0f172a", lineHeight: 1.08, mb: 0.4 }}>
          {text.signIn}
        </MDTypography>
        <MDTypography variant="body2" color="text" sx={{ opacity: 0.8, mb: 2 }}>
          {text.siteLabel}
        </MDTypography>

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}

        <MDBox
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Stack spacing={1.25}>
            <TextField
              type="email"
              label={text.email}
              fullWidth
              value={email}
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              type={showPassword ? "text" : "password"}
              label={text.password}
              fullWidth
              value={password}
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((prev) => !prev)}>
                      <Icon fontSize="small">{showPassword ? "visibility_off" : "visibility"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              sx={{ py: 1.35, borderRadius: 2.2, mt: 0.5 }}
              disabled={loading}
              onClick={async () => {
                try {
                  setLoading(true);
                  setError("");
                  const ok = await login(String(email || "").trim(), password);
                  if (ok) navigate(redirectTo, { replace: true });
                  else setError(text.invalidCredentials);
                } catch {
                  setError(text.loginFailed);
                } finally {
                  setLoading(false);
                }
              }}
            >
              {loading ? text.signingIn : text.signIn}
            </MDButton>
            {googleClientId || appleClientId || microsoftClientId ? (
              <MDBox sx={{ mt: 0.9 }}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", textAlign: "center", opacity: 0.78, mb: 0.65 }}
                >
                  {text.continueWith}
                </MDTypography>
                <Stack direction="row" spacing={0.8} justifyContent="center" alignItems="center">
                  {googleClientId ? (
                    <MDButton
                      variant="outlined"
                      color="dark"
                      sx={socialIconButtonSx}
                      disabled={loading}
                      onClick={() => handleSocialSignIn("google")}
                      title="Google"
                      aria-label="Continue with Google"
                    >
                      <GoogleIcon sx={{ color: "#EA4335", fontSize: 18 }} />
                    </MDButton>
                  ) : null}
                  {appleClientId ? (
                    <MDButton
                      variant="outlined"
                      color="dark"
                      sx={socialIconButtonSx}
                      disabled={loading}
                      onClick={() => handleSocialSignIn("apple")}
                      title="Apple"
                      aria-label="Continue with Apple"
                    >
                      <AppleIcon sx={{ color: "#111827", fontSize: 18 }} />
                    </MDButton>
                  ) : null}
                  {microsoftClientId ? (
                    <MDButton
                      variant="outlined"
                      color="dark"
                      sx={socialIconButtonSx}
                      disabled={loading}
                      onClick={() => handleSocialSignIn("microsoft")}
                      title="Microsoft"
                      aria-label="Continue with Microsoft"
                    >
                      <MicrosoftMark />
                    </MDButton>
                  ) : null}
                </Stack>
              </MDBox>
            ) : null}
          </Stack>
        </MDBox>

        <MDTypography variant="button" color="text" sx={{ display: "block", opacity: 0.88, mt: 1.5 }}>
          {text.noAccount}{" "}
          <Link to="/register" style={{ fontWeight: 700 }}>
            {text.createAccount}
          </Link>
        </MDTypography>
      </Card>
    </Box>
  );
}

export default Basic;
